'use client'

import type { AnreiseBlock as AnreiseBlockProps } from 'src/payload-types'

import React from 'react'
import { motion } from 'motion/react'
import { useInView } from 'react-intersection-observer'
import RichText from '@/components/RichText'
import AnimatedText from '@/components/Antonie/Components/animated-text'
import { Mask } from '@/components/Mask'

type Props = {
  className?: string
} & AnreiseBlockProps

export const AnreiseBlock: React.FC<Props> = ({ title, googleMapsEmbed, content }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  })

  const textVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
    },
  }

  const contentVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
    },
  }

  const mapVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
    },
  }

  return (
    <section className="bg-white py-12 md:py-24">
      <div className="container w-full px-6">
        <div className="flex justify-center">
          <Mask width={50} height={50} color="black" />
        </div>
        <motion.div ref={ref} className="mx-auto w-full text-center">
          {title && (
            <AnimatedText
              text={title}
              type="h2"
              variant="simple"
              className="mb-8 font-heading text-2xl font-medium tracking-wide text-gray-900 md:text-4xl"
              delay={0.2}
            />
          )}

          <motion.div
            variants={textVariants}
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
            className="mx-auto mb-10 h-px w-16 bg-antonie-red"
          />

          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
            <motion.div
              variants={contentVariants}
              initial="hidden"
              animate={inView ? 'visible' : 'hidden'}
              className="text-left"
            >
              <RichText
                data={content}
                enableGutter={false}
                enableProse={false}
                className="text-left"
              />
            </motion.div>

            <motion.div
              variants={mapVariants}
              initial="hidden"
              animate={inView ? 'visible' : 'hidden'}
              className="h-96 w-full overflow-hidden  shadow-lg"
            >
              <div
                className="h-full w-full"
                dangerouslySetInnerHTML={{ __html: googleMapsEmbed }}
              />
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
